import type { FetchResult } from 'apollo-link';

import { BadStatusError } from '@confluence/network';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { CollabEditProviderMeasures } from '@confluence/action-measures/entry-points/CollabEditProviderMeasures';

import { COLLAB_EDIT_PROVIDER_MEASURES } from './CollabEditProviderMeasures';
import type { ExperimentalResetCollabService as ExperimentalResetCollabServiceResp } from './__types__/ExperimentalResetCollabService';
import { fetchDraft } from './fetchDraft';
import type { InitialDraft, FetchDraft } from './fetchDraft';
import { experimentalResetCollabServiceMutate } from './experimentalResetCollabServiceMutate';

const STATUS_NOT_FOUND = 404;

const performRecovery = async (
	contentId: string,
): Promise<
	FetchResult<ExperimentalResetCollabServiceResp> | { data: ExperimentalResetCollabServiceResp }
> => {
	return experimentalResetCollabServiceMutate({
		variables: {
			behind: 'COLLAB',
			contentId,
		},
	});
};

const fireDraftFailureEvent = (
	error: BadStatusError | Error,
	recoverySucceeded: boolean | null,
	recoveryError?: Error,
) => {
	void getAnalyticsWebClient().then((client) => {
		client.sendOperationalEvent({
			source: 'NativeCollabProvider',
			action: 'getDraft',
			actionSubject: 'failure',
			attributes: {
				error,
				recoverySucceeded,
				recoveryError,
			},
		});
	});
};

const fireDraftFetchEvent = (state: 'success' | 'failure') => {
	void getAnalyticsWebClient().then((client: any) => {
		client.sendOperationalEvent({
			source: 'NativeCollabProvider',
			action: 'sentRequest',
			actionSubject: 'NcsDraftEndpoint',
			attributes: {
				state,
			},
		});
	});
};

const fetchDraftPromiseWithMetrics = (fetchDraftPromise: Promise<FetchDraft>) => {
	return fetchDraftPromise
		.then((response) => {
			if (response.error) fireDraftFetchEvent('failure');
			else fireDraftFetchEvent('success');

			return response;
		})
		.catch((error) => {
			fireDraftFetchEvent('failure');
			throw error;
		});
};

export const getDraft = (contentId: string, draftShareId?: string): Promise<InitialDraft | null> =>
	getDraftFromPromise(contentId, fetchDraft(contentId, draftShareId));

export const getDraftFromPromise = (
	contentId: string,
	fetchDraftPromise: Promise<FetchDraft>,
): Promise<InitialDraft | null> => {
	CollabEditProviderMeasures.markMeasureStart(
		COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_FETCH_NCS_DRAFT,
	);

	return fetchDraftPromiseWithMetrics(fetchDraftPromise)
		.then(async ({ draft }) => {
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_FETCH_NCS_DRAFT,
			);
			if (draft) {
				const version = draft?.version;
				if (!version) {
					void getAnalyticsWebClient().then((client) => {
						client.sendOperationalEvent({
							source: 'NativeCollabProvider',
							action: 'getDraft',
							actionSubject: 'version is 0',
							attributes: {
								version,
							},
						});
					});
					return null;
				}
				return draft;
			}
			return null;
		})
		.catch((error: BadStatusError | Error) => {
			CollabEditProviderMeasures.markMeasureEnd(
				COLLAB_EDIT_PROVIDER_MEASURES.COLLAB_EDIT_PROVIDER_FETCH_NCS_DRAFT,
			);
			if (error instanceof BadStatusError && error?.response?.status === STATUS_NOT_FOUND) {
				return performRecovery(contentId)
					.then((_recoveryResponse: FetchResult<ExperimentalResetCollabServiceResp>) => {
						fireDraftFailureEvent(error, true);
						return null;
					})
					.catch((recoveryError) => {
						fireDraftFailureEvent(error, false, recoveryError);

						return null;
					});
			}

			fireDraftFailureEvent(error, null);
			return null;
		});
};
